import axios from "axios";

// Central place for reading the auth token, checking expiry, and triggering a
// logout. Every axios client and the AuthContext use these helpers so auth
// behaviour is consistent across the whole app.

export const AUTH_LOGOUT_EVENT = "gg:auth-logout";

// Token can live in localStorage ("remember me") or sessionStorage.
export const getToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

export const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

// True when there is no token or the JWT `exp` claim is in the past.
export const isTokenExpired = (token = getToken()) => {
  if (!token) return true;
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return false; // no exp claim → treat as non-expiring
  // 5s clock-skew safety margin.
  return Date.now() >= payload.exp * 1000 - 5000;
};

export const clearAuthStorage = () => {
  localStorage.removeItem("authToken");
  sessionStorage.removeItem("authToken");
  try {
    delete axios.defaults.headers.common.Authorization;
  } catch {
    /* no-op */
  }
};

// Public auth endpoints must NOT trigger a logout (e.g. a 401 from /login just
// means "wrong password").
const PUBLIC_AUTH = /\/auth\/(login|register|resend-verification|verify-email|verify|forgot-password|reset-password)/i;
export const isPublicAuthEndpoint = (url = "") => PUBLIC_AUTH.test(url || "");

let loggingOut = false;

/**
 * Log the user out: clear stored credentials and broadcast an event so the
 * AuthContext can drop the React user state (and ProtectedRoute can redirect).
 * De-duped so a burst of failing requests only logs out once.
 */
export const forceLogout = (reason = "expired") => {
  if (loggingOut) return;
  loggingOut = true;
  clearAuthStorage();
  try {
    window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT, { detail: { reason } }));
  } catch {
    /* no-op */
  }
  setTimeout(() => {
    loggingOut = false;
  }, 800);
};

/**
 * Decide whether an axios error should log the user out. Only for authenticated
 * requests (a token exists) that fail with an auth status on a non-public
 * endpoint — so anonymous browsing never gets bounced to sign-in.
 */
export const shouldLogoutForError = (error) => {
  const status = error?.response?.status;
  const url = error?.config?.url || "";
  if (isPublicAuthEndpoint(url)) return false;
  if (!getToken()) return false; // anonymous user — don't force logout
  if (status === 401) return true;
  if (status === 403) {
    const msg = (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      ""
    ).toLowerCase();
    return msg.includes("token") || msg.includes("expired") || msg.includes("unauthorized");
  }
  return false;
};

// Attach the token + auth-aware response handling to any axios instance.
export const attachAuthInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = getToken();
    if (token && !isPublicAuthEndpoint(config.url)) {
      if (isTokenExpired(token)) {
        forceLogout("expired");
        return Promise.reject(new axios.Cancel("Session expired"));
      }
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (shouldLogoutForError(error)) forceLogout("expired");
      return Promise.reject(error);
    },
  );
};
